/**
 * useGameVersionPoll — keeps a game page in sync with server state.
 *
 * Polls GET /teams/:teamId/games/:gameId/version every 15s while the
 * document is visible. Compares the returned timestamp to the baseline
 * (set by the caller from the full game load). When the server's
 * version advances past the baseline, fires the `onChanged` callback
 * — typically the caller's `refresh()` function — so the page
 * auto-refreshes without needing a manual "Refresh" click.
 *
 * Pauses when:
 *   - the document is hidden (tab in background, screen off)
 *   - the browser is offline (visibilitychange / online / offline)
 *   - teamId or gameId is missing (not yet resolved)
 *
 * Re-fires immediately when the tab becomes visible again (so a coach
 * who tab-switches away for a few minutes sees a fresh page the
 * instant they come back, without waiting out the interval).
 *
 * Use:
 *   const baselineRef = useRef(null);
 *   useGameVersionPoll(teamId, gameId, baselineRef, { onChanged: refresh });
 *   // After each full game load:
 *   baselineRef.current = data.version;
 *
 * The caller owns the baseline via a ref so mutations the user
 * themselves initiated (RSVP, promote, pay) never cause their own
 * page to re-render an in-progress local state — the caller reloads
 * the game after every local action, which bumps the baseline to the
 * fresh server version before the next poll.
 *
 * Backwards-compatible: still returns { stale, clear, forceCheck } for
 * callers who want to render a manual banner. When `onChanged` is
 * provided, stale flips back to false automatically after the callback
 * runs so the optional banner doesn't linger.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api.js';

const POLL_INTERVAL_MS = 15_000;

export function useGameVersionPoll(teamId, gameId, baselineRef, options = {}) {
  const { onChanged } = options;
  const [stale, setStale] = useState(false);
  const timerRef    = useRef(null);
  const inflightRef = useRef(false);
  // Keep onChanged in a ref so the polling effect doesn't need to list
  // it as a dependency — callers typically pass a fresh fn every render
  // (e.g. an inline arrow), and re-running the effect on every render
  // would reset the interval constantly.
  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);

  const check = useCallback(async () => {
    if (!teamId || !gameId) return;
    if (!baselineRef.current) return;          // no baseline yet → nothing to compare
    if (inflightRef.current) return;           // avoid overlapping polls on slow networks
    if (document.hidden) return;
    if (!navigator.onLine) return;
    inflightRef.current = true;
    try {
      const { data } = await api.get(`/teams/${teamId}/games/${gameId}/version`);
      const serverVersion = data?.version;
      if (serverVersion && baselineRef.current && serverVersion > baselineRef.current) {
        // Bump the baseline FIRST so a slow refresh doesn't cause the
        // next poll tick to also fire onChanged with a duplicate
        // signal (the caller's refresh will re-seed the baseline from
        // its own response when it completes; this intermediate bump
        // prevents an immediate second-call loop).
        baselineRef.current = serverVersion;
        if (typeof onChangedRef.current === 'function') {
          try { onChangedRef.current(); }
          catch (err) { console.error('useGameVersionPoll onChanged failed:', err); }
          // Don't keep `stale=true` lingering after auto-refresh.
          setStale(false);
        } else {
          setStale(true);
        }
      }
    } catch {
      // 4xx/5xx/offline — swallow, try again next interval. A missed
      // refresh isn't critical enough to surface error toasts for.
    } finally {
      inflightRef.current = false;
    }
  }, [teamId, gameId, baselineRef]);

  const clear = useCallback(() => setStale(false), []);

  useEffect(() => {
    if (!teamId || !gameId) return undefined;
    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(check, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    const onVis = () => {
      if (document.hidden) {
        stop();
      } else {
        check();   // immediate tick so tab-wake feels instant
        start();
      }
    };
    const onOnline = () => check();

    start();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    window.addEventListener('online', onOnline);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      window.removeEventListener('online', onOnline);
    };
  }, [teamId, gameId, check]);

  return { stale, clear, forceCheck: check };
}
